import { useEffect, useRef, useState } from "react";
import styles from "./EstimateDetail.module.scss";
import classNames from "classnames/bind";
import OutlinedButton from "~/components/common/outlined-button/OutlinedButton";
import { ArrowLeftIcon } from "lucide-react";
import EstimateStep1 from "./step/step1/EstimateStep1";
import EstimateStep2 from "./step/step2/EstimateStep2";
import EstimateStep3 from "./step/step3/EstimateStep3";
import EstimateStep4 from "./step/step4/EstimateStep4";
import EstimateStep5 from "./step/step5/EstimateStep5";
import EstimateStep6 from "./step/step6/EstimateStep6";
import EstimateStep7 from "./step/step7/EstimateStep7";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { EstimateCheckValidate, EstimateDetailFormData, EstimateFormData } from "./EstimateDetailFormData";
import EstimatePDF from "./viewPDF/EstimatePDF";
import { handleCreateEstimate, handleGetAllEstimate, handleGetAllEstimateDetail, handleUpdateEstimate } from "~/thunks/job/jobThunk";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { handleGetJobDetails, handleGetOverviewJobDetails } from "~/thunks/job-details/jobDetailsThunk";
import { EstimateModeViewEnum, EstimateStatusEnum } from "~/pages/admin/jobs/add-job/constants/add-estimate.constants";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import { notify } from "~/utils/helper";
import { useTranslation } from "react-i18next";
import ViewPdfModal from "./viewPDF/ViewPdfModal";

const cx = classNames.bind(styles);

const steps: any[] = [
    "Title",
    "About Us",
    "Inspection",
    "Quote Details",
    "Authorization",
    "Terms & Condition",
    "CBR Insurance",
]

const step_names: any[] = [
    "STEP_1_TITLE",
    "STEP_2_ABOUT_US",
    "STEP_3_INSPECTION",
    "STEP_4_QUOTE_DETAILS",
    "STEP_5_AUTHORIZATION",
    "STEP_6_TERMS_CONDITIONS",
    "STEP_7_CBR_INSURANCE",
]

const EstimateDetail = () => {
    const { id, estimateId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation(["admin/job"]);
    const { jobDetails, overviewJobDetails } = useAppSelector((state) => state.jobDetails);

    const [curStep, setStep] = useState(-1);
    const [isViewPDF, setViewPDF] = useState(false);

    const refPdf = useRef(null);
    const [modeForm, setModeForm] = useState<"CREATE" | "VIEW" | "EDIT">("CREATE");
    const [titleForm, setTitleForm] = useState("Create Estimate");
    const [helpStep, setHelpStep] = useState<any>(<></>);
    const [isDraft, setDraft] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [opendPreview, setOpendPreview] = useState(false);

    const [formData, setFormData] = useState<any>({
        jobId: id,
        lastStep: 1,
        estimateDate: new Date(),
        titleStep1: "Title & Details",
        titleStep2: "About Us",
        titleStep3: "Inspection",
        titleStep4: "Quote Details",
        titleStep5: "Authorization Page",
        titleStep6: "Terms & Condition",
        titleStep7: "CBR Insurance",
    });
    const formDataRef = useRef(formData);
    formDataRef.current = formData;
    const [error, setError] = useState<any>({});

    const [formDataStep, setFormDataStep] = useState<any>({});
    const [errorStep, setErrorStep] = useState<any>({});
    useEffect(() => {
        if (id) {
            dispatch(handleGetJobDetails(id));
            dispatch(handleGetOverviewJobDetails(id));
        }
    }, [id, dispatch]);


    const renderStepData = () => {
        if (curStep == 0) {
            return <EstimateStep1
                onChange={handleFormDataStep1}
                data={formDataStep}
                mode={modeForm}
                errorForm={error}
                onPreview={() => { setOpendPreview(true) }}
                primaryContact={jobDetails?.relatedContacts?.primaryContact}
            />
        }
        else if (curStep == 1) {
            return <EstimateStep2
                onChange={handleFormDataStep2}
                data={formDataStep}
                mode={modeForm}
                errorForm={error}
                onPreview={() => { setOpendPreview(true) }}
                salesRep={overviewJobDetails?.salesRep}
            />
        }
        else if (curStep == 2) {
            return <EstimateStep3 onChange={handleFormDataStep3}
                data={formDataStep}
                mode={modeForm}
                errorForm={error}
                onPreview={() => { setOpendPreview(true) }}
            />
        }
        else if (curStep == 3) {
            return <EstimateStep4 onChange={handleFormDataStep4}
                data={formDataStep}
                mode={modeForm}
                errorForm={error}
                onPreview={() => { setOpendPreview(true) }}
            />
        }
        else if (curStep == 4) {
            return <EstimateStep5 onChange={handleFormDataStep5}
                data={formDataStep}
                mode={modeForm}
                errorForm={error}
                onPreview={() => { setOpendPreview(true) }}
            />
        }
        else if (curStep == 5) {
            return <EstimateStep6 onChange={handleFormDataStep6}
                data={formDataStep}
                mode={modeForm}
                errorForm={error}
                onPreview={() => { setOpendPreview(true) }}
                salesRep={overviewJobDetails?.salesRep}
            />
        }
        else if (curStep == 6) {
            return <EstimateStep7 onChange={handleFormDataStep7}
                data={formDataStep}
                mode={modeForm}
                errorForm={error}
                onPreview={() => { setOpendPreview(true) }}
                salesRep={overviewJobDetails?.salesRep}
            />
        }
        return (<>Step {curStep + 1}</>)
    }
    const handleContinue = () => {
        if (curStep < 6) {
            changeStage(curStep + 1);
        } else {
            let error = EstimateCheckValidate(t, formData, curStep);
            if (!(error['_noError'] || Object.keys(error).length == 0)) {
                setError(error);
                return;
            }
            setError({});

            if (modeForm == EstimateModeViewEnum.VIEW) {
                setViewPDF(true);
            } else {
                setIsPreviewMode(true);
            }
        }
    };

    const handleConfirmCreate = () => {
        if (modeForm == EstimateModeViewEnum.CREATE) {
            onCreateEstimate(EstimateStatusEnum.SENT);
        } else {
            onUpdateEstimate(EstimateStatusEnum.SENT);
        }
    };

    const handlePreviewClick = () => {
        let error = EstimateCheckValidate(t, formData, curStep);
        if (!(error['_noError'] || Object.keys(error).length == 0)) {
            setError(error);
            return;
        }
        setError({});
        setIsPreviewMode(true);
    };

    const handleDraft = () => {
        if (!isDraft) return;
        if (modeForm == EstimateModeViewEnum.CREATE)
            onCreateEstimate(EstimateStatusEnum.DRAFT);
        else if (modeForm == EstimateModeViewEnum.EDIT) {
            onUpdateEstimate(EstimateStatusEnum.DRAFT);
        }
    }

    const handleSaveDraft = () => {
        if (!isDraft) return;
        const step1Error = EstimateCheckValidate(t, formData, 0);
        if (!(step1Error["_noError"] || Object.keys(step1Error).length === 0)) {
            setError(step1Error);
            return;
        }
        setError({});
        if (modeForm === EstimateModeViewEnum.CREATE) {
            onCreateEstimate(EstimateStatusEnum.DRAFT);
        } else if (modeForm === EstimateModeViewEnum.EDIT) {
            onUpdateEstimate(EstimateStatusEnum.DRAFT);
        }
    }


    const handleSave = async () => {
        await onUpdateEstimate(EstimateStatusEnum.SENT);
        setModeForm(EstimateModeViewEnum.VIEW as "VIEW")
    }

    const changeStage = (step: number) => {
        if (isViewPDF) return;

        let error = EstimateCheckValidate(t, formData, curStep);
        // Proceed if _noError is true or if there are no keys.
        if (!(error['_noError'] || Object.keys(error).length == 0)) {
            setError(error);
            console.log(error)
            return;
        }
        setError({});

        setStep(step);
        setViewPDF(false);
        if (step == 0) {
            setFormDataStep({
                name: formData.name,
                date: formData.estimateDate ? formData.estimateDate : new Date(),
                company: formData.companyName,
                firstName: formData.firstName,
                lastName: formData.lastName,

                address: formData.address,
                city: formData.city,
                province: formData.stateProvince,
                code: formData.zipCodePostalCode,
                file: formData.primaryImage,
                fileUrl: formData.titleFileUrl,
                titleStep1: formData.titleStep1,
            })
        }
        else if (step == 1) {
            setFormDataStep({
                companyDescription: formData.usDescription,
                titleStep2: formData.titleStep2,
                firstName: formData.firstName,
            })
        }
        else if (step == 2) {
            setFormDataStep({
                inspections: formData.inspections ? formData.inspections : [],
                titleStep3: formData.titleStep3
            })
        }
        else if (step == 3) {
            setFormDataStep({
                notes: formData.quoteNote,
                quotes: formData.quotes?.length ? formData.quotes : [
                    {
                        quoteName: "Owens 1",
                        sections: [{
                            title: "Section 1",
                            isVisible: true,
                            sectionItems: [{
                                itemName: "",
                                type: "MATERIAL",
                                uom: "",
                                quantity: 0,
                                unitPrice: 0,
                            }],
                        }],
                        quoteProfitMargin: 0,
                        note: "",
                    }
                ],
                quoteProfitMargin: formData.quoteProfitMargin ?? 0,
                titleStep4: formData.titleStep4,

            })
        }
        else if (step == 4) {
            setFormDataStep({
                disclaimer: formData.disclaimer,
                signer: formData.signer ? formData.signer : {},
                footerNotes: formData.footerNotes,
                optional: formData.optional ? formData.optional : {
                    title: '',
                    isVisible: true,
                    sections: []
                },
                selection: formData.selection,
                isDeposit: formData.isDeposit,
                signerTitle: formData.signerTitle,
                titleStep5: formData.titleStep5,
            })
        }
        else if (step == 5) {
            setFormDataStep({
                termsNConditions: formData.termsNConditions,
                titleStep6: formData.titleStep6,
                firstName: formData.firstName,
            })
        }
        else if (step == 6) {
            setFormDataStep({
                insuranceDetails: formData.insuranceDetails,
                isAcknowledged: formData.isAcknowledged == undefined ? true : formData.isAcknowledged,
                titleStep7: formData.titleStep7,
                firstName: formData.firstName,
            })
        }
    }

    const onClickSave = () => {
        if (refPdf && refPdf.current) {
            let pdfA: any = refPdf.current;
            pdfA.exportPdf();
        }
    }

    const handleButtonBackClick = () => {
        navigate(`/admin/job/detail/${id}?estimates`);
    }

    const handleFormDataStep1 = (formDataStep: any) => {
        //check validate

        let newFormData = {
            ...formData,
            lastStep: step_names[0],
            name: formDataStep.name,
            estimateDate: formDataStep.date ? formDataStep.date : new Date(),
            companyName: formDataStep.company,
            firstName: formDataStep.firstName,
            lastName: formDataStep.lastName,
            address: formDataStep.address,
            city: formDataStep.city,
            stateProvince: formDataStep.province,
            zipCodePostalCode: formDataStep.code,
            primaryImage: formDataStep.file,
            titleFileUrl: formDataStep.fileUrl,
            titleStep1: formDataStep.titleStep1,
        };

        //convert to form
        setFormData(newFormData)



    }

    const handleFormDataStep2 = (formDataStep: any) => {
        //check validate
        setFormDataStep(formDataStep);
        setFormData((prev: any) => ({
            ...prev,
            lastStep: step_names[1],
            usDescription: formDataStep.companyDescription,
            titleStep2: formDataStep.titleStep2,
        }));
    }

    const handleFormDataStep3 = (formDataStep: any) => {
        setFormData({
            ...formData,
            lastStep: step_names[2],
            inspections: formDataStep.inspections,
            titleStep3: formDataStep.titleStep3,
        })
    }

    const handleFormDataStep4 = (formDataStep: any) => {
        setFormData((prev: any) => {
            const next = {
                ...prev,
                lastStep: step_names[3],
                quoteNote: formDataStep.notes ?? formDataStep.quotes?.[0]?.note ?? prev.quoteNote,
                quoteProfitMargin: formDataStep.quotes?.[0]?.quoteProfitMargin
                    ?? formDataStep.quoteProfitMargin
                    ?? prev.quoteProfitMargin,
                quotes: formDataStep.quotes ?? prev.quotes,
                titleStep4: formDataStep.titleStep4 ?? prev.titleStep4,
            };
            formDataRef.current = next;
            return next;
        });
    }

    const handleFormDataStep5 = (formDataStep: any) => {
        setFormData((prev: any) => {
            const next = {
                ...prev,
                lastStep: step_names[4],
                disclaimer: formDataStep.disclaimer,
                signer: formDataStep.signer,
                isDeposit: formDataStep.isDeposit,
                signerTitle: formDataStep.signerTitle,
                footerNotes: formDataStep.footerNotes,
                optional: formDataStep.optional,
                selection: formDataStep.selection,
                titleStep5: formDataStep.titleStep5,
            };
            formDataRef.current = next;
            return next;
        });

        if (Object.keys(error).length > 0) {
            setError((prev: any) => {
                const next = { ...prev };

                if (formDataStep.signer?.firstName?.trim()) delete next.firstName;
                if (formDataStep.signer?.lastName?.trim()) delete next.lastName;
                if (formDataStep.signer?.email?.trim()) delete next.email;
                if (formDataStep.disclaimer?.trim()) delete next.disclaimer;
                if (formDataStep.footerNotes?.trim()) delete next.footerNotes;

                return next;
            });
        }
    };

    const handleFormDataStep6 = (formDataStep: any) => {
        setFormDataStep(formDataStep);
        setFormData((prev: any) => ({
            ...prev,
            lastStep: step_names[5],
            termsNConditions: formDataStep.termsNConditions,
            titleStep6: formDataStep.titleStep6,
        }));
    }

    const handleFormDataStep7 = (formDataStep: any) => {
        setFormData({
            ...formData,
            lastStep: step_names[6],
            insuranceDetails: formDataStep.insuranceDetails,
            isAcknowledged: formDataStep.isAcknowledged,
            titleStep7: formDataStep.titleStep7,
        })
    }

    const handleChange = () => {

    }

    const pushData = () => {
        let data = EstimateDetailFormData(formData);
    }

    const onCreateEstimate = async (status: string) => {
        setIsLoading(true);
        let dataReponse: any = {};
        let errorParse: any = {};

        // let {data, error} = EstimateDetailFormData(formData);

        try {
            let { data, error } = EstimateDetailFormData(formDataRef.current);

            if (error) {
                notify("error", "Create failed!");
                setIsLoading(false);
                return;
            }

            data.status = status
            dataReponse = { ...data }
            errorParse = { ...error }
        } catch (e) {
            notify("error", "Create failed!");
            setIsLoading(false);
            return;
        }


        dispatch(handleCreateEstimate({ data: dataReponse })).unwrap()
            .then((res: any) => {
                setIsLoading(false);
                if (status === EstimateStatusEnum.DRAFT) {
                    notify("success", "Draft saved");
                    const newId = res?.data?.id ?? res?.id;
                    if (newId) {
                        navigate(`/admin/job/detail/${id}/estimate/${newId}?edit`, { replace: true });
                    }
                    return;
                }
                notify("success", "Estimate saved");
                navigate(`/admin/job/detail/${id}?estimates`);
            })
            .catch((error: any) => {
                setIsLoading(false);
                notify("error", "Create failed!");
            });
    }

    const onUpdateEstimate = async (status: string) => {
        setIsLoading(true);

        let dataReponse: any = {};
        let errorParse: any = {};

        try {
            let { data, error } = EstimateDetailFormData(formDataRef.current);

            if (error) {
                console.error(error)
                notify("error", "Update failed!");
                setIsLoading(false);
                return;
            }

            data.status = status
            dataReponse = { ...data }
            errorParse = { ...error }
        } catch (e) {
            console.error(e)
            notify("error", "Update failed!");
            setIsLoading(false);
            return;
        }

        let dataEdit = { ...dataReponse };
        dataEdit.lastStep = step_names[6];
        delete dataEdit.jobId;
        delete dataEdit.estimateLayoutId;

        dispatch(handleUpdateEstimate({ id: estimateId!, data: dataEdit })).unwrap()
            .then(() => {
                setIsLoading(false);
                if (status === EstimateStatusEnum.DRAFT) {
                    notify("success", "Draft saved");
                } else {
                    notify("success", "Estimate saved");
                }
                setViewPDF(modeForm == EstimateModeViewEnum.CREATE);
            })
            .catch((error: any) => {
                setIsLoading(false);
                notify("error", "Update failed!");
            })
            .finally(() => {

            });
    }

    const getEstimateDetail = (estimateDetailId: string) => {
        setIsLoading(true);
        dispatch(handleGetAllEstimateDetail({ id: estimateDetailId })).unwrap()
            .then((e: any) => {
                let data: any = EstimateFormData(e?.data ?? e);
                const step1Data = {
                    name: data.name ?? "",
                    date: data.estimateDate ? data.estimateDate : new Date(),
                    company: data.companyName ?? "",
                    firstName: data.firstName ?? "",
                    lastName: data.lastName ?? "",
                    address: data.address ?? "",
                    city: data.city ?? "",
                    province: data.stateProvince ?? "",
                    code: data.zipCodePostalCode ?? "",
                    file: undefined,
                    fileUrl: data.primaryImage ? data.primaryImage.url : "",
                    titleStep1: data.titleStep1 ?? "Title & Details",
                };
                setDraft(data.status == EstimateStatusEnum.DRAFT);
                setFormData({
                    ...data,
                    jobId: id,
                    lastStep: data.lastStep || step_names[0],
                    primaryImage: undefined,
                    titleFileUrl: data.primaryImage ? data.primaryImage.url : "",
                });
                setStep(0);
                setViewPDF(false);
                setFormDataStep(step1Data);
                setError({});
                setIsLoading(false);
            })
            .catch((error: any) => {
                console.log(error);
                notify("error", "Failed to load estimate");
                setIsLoading(false);
            })
    }

    useEffect(() => {
        if (!estimateId) {
            setModeForm("CREATE");
            setTitleForm("Create Estimate");
            setHelpStep(<>Follow the steps to create your<br />estimate</>);
            changeStage(0);
            setDraft(true);
            setIsLoading(false);
        }
        else if (searchParams.has("edit")) {
            setModeForm("EDIT")
            setTitleForm("Edit Estimate");
            setHelpStep(<>Follow the steps to edit your<br />estimate</>);
            getEstimateDetail(estimateId);

        } else {
            setModeForm("VIEW")
            setTitleForm("Estimate Detail");
            setHelpStep(<>Follow the steps to view your<br />estimate</>);
            getEstimateDetail(estimateId);
        }
    }, [estimateId])

    return <>
        <div className={cx('estimate-detail-container')}>
            {isLoading && (
                <div className={cx("loading-overlay")}>
                    <LoadingMainContent />
                </div>
            )}
            <div className={cx("header")}>
                <OutlinedButton
                    title="Back to Estimates"
                    icon={<ArrowLeftIcon className={cx("back-icon")} />}
                    onClick={handleButtonBackClick}
                    className={cx("back-button")}
                />

                <div className="flex items-center gap-[12px]">

                    {
                        (modeForm != EstimateModeViewEnum.VIEW) && (curStep == 0) && (<>
                            <button
                                className={cx('step-edit')}
                                onClick={() => {
                                    //  setModeForm(EstimateModeViewEnum.EDIT as "EDIT")
                                }}
                            >
                                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M20.75 9.83572V10.7557C20.7488 12.9121 20.0505 15.0104 18.7593 16.7375C17.4682 18.4647 15.6533 19.7282 13.5854 20.3396C11.5174 20.951 9.30726 20.8776 7.28447 20.1303C5.26168 19.383 3.53465 18.0018 2.36096 16.1928C1.18727 14.3838 0.629791 12.2438 0.771677 10.092C0.913562 7.94029 1.74721 5.89205 3.14828 4.2528C4.54935 2.61354 6.44279 1.47111 8.54619 0.99587C10.6496 0.520634 12.8503 0.738061 14.82 1.61572M20.75 2.75572L10.75 12.7657L7.75001 9.76572" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                Mark As Signed
                            </button>
                        </>)
                    }


                    {
                        modeForm == EstimateModeViewEnum.VIEW && (<>
                            <button
                                className={cx('step-edit')}
                                onClick={() => {
                                    setModeForm(EstimateModeViewEnum.EDIT as "EDIT")
                                }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 4.00023H4C3.46957 4.00023 2.96086 4.21094 2.58579 4.58601C2.21071 4.96109 2 5.46979 2 6.00023L2 20.0002C2 20.5307 2.21071 21.0394 2.58579 21.4144C2.96086 21.7895 3.46957 22.0002 4 22.0002H18C18.5304 22.0002 19.0391 21.7895 19.4142 21.4144C19.7893 21.0394 20 20.5307 20 20.0002V13.0002M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>


                                Edit
                            </button>
                        </>)
                    }
                    {!isViewPDF && !isPreviewMode && (
                        <button
                            className={cx('step-preview')}
                            onClick={handlePreviewClick}
                        >
                            Preview
                        </button>
                    )}

                    {isPreviewMode && (
                        <>
                            <button
                                className={cx('step-preview')}
                                onClick={() => setIsPreviewMode(false)}
                            >
                                ← Back to Edit
                            </button>
                            {isDraft && (
                                <button
                                    className={cx('step-save-draft')}
                                    onClick={handleSaveDraft}
                                >
                                    Save Draft
                                </button>
                            )}
                            <button
                                className={cx('step-share')}
                                onClick={handleConfirmCreate}
                            >
                                {modeForm === EstimateModeViewEnum.EDIT ? 'Save' : 'Continue'}
                            </button>
                        </>
                    )}

                    {!isPreviewMode && !isViewPDF && isDraft && (modeForm == EstimateModeViewEnum.CREATE || modeForm == EstimateModeViewEnum.EDIT) && (
                        <button className={cx('step-save-draft')} onClick={handleSaveDraft}>
                            Save Draft
                        </button>
                    )}
                    {!isPreviewMode && modeForm == EstimateModeViewEnum.CREATE && !isViewPDF && (
                        <button className={cx('step-share')} onClick={handleContinue}>
                            Continue
                        </button>
                    )}
                    {!isPreviewMode && modeForm == EstimateModeViewEnum.VIEW && (
                        <button className={cx('step-share')} onClick={() => { }}>
                            Share
                        </button>
                    )}
                    {!isPreviewMode && modeForm == EstimateModeViewEnum.EDIT && (
                        <button className={cx('step-share')} onClick={handleSave}>
                            Save
                        </button>
                    )}
                </div>
            </div>
            <div className={cx('estimate-detail-content')}>
                <div className={cx('estimate-detail-step')}>
                    <div className={cx('header-step', 'mb-[32px]')}>
                        <p className="text-[24px] leading-[32px] font-[700] text-[#F9FBFE]">{titleForm}</p>
                        <p className="text-[14px] leading-[20px] font-[400] text-[#949699]">{helpStep}</p>
                    </div>

                    <div className={cx({
                        'steps': true,
                        'steps-preview': isViewPDF
                    })}>
                        {
                            steps.map((step, index) =>
                                <div className={cx({
                                    'step-item': true,
                                    'selected': curStep == index
                                })}
                                    onClick={() => {
                                        changeStage(index)
                                    }}>
                                    <div className={cx('step-number')}>
                                        {index + 1}
                                    </div>
                                    <div >
                                        <h3>Step {index + 1}</h3>
                                        <h4>{step}</h4>
                                    </div>
                                </div>)
                        }
                    </div>

                    {
                        // modeForm == EstimateModeViewEnum.CREATE && (
                        //     <div className={cx({
                        //         'steps-preview':isViewPDF
                        //     })}>
                        //         <button className={cx("footer-save-button")}
                        //             // onClick={onClickSave}
                        //         >
                        //             Draft
                        //         </button>
                        //         <button
                        //             className={cx("footer-continue-button")}
                        //             onClick={handleContinue}
                        //         >
                        //             Continue
                        //         </button>
                        //     </div>
                        // )
                    }





                </div>

                <div className={cx('estimate-detail-body-content')}>
                    {isPreviewMode ? (
                        <EstimatePDF data={formData} ref={refPdf} />
                    ) : isViewPDF ? (
                        <EstimatePDF data={formData} ref={refPdf} />
                    ) : (
                        renderStepData()
                    )}
                </div>
            </div>

            <ViewPdfModal
                data={formData}
                onClose={() => { setOpendPreview(false) }}
                open={opendPreview}
                step={curStep} />

        </div>
    </>
}

export default EstimateDetail;